import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  archiveApi,
  folderApi,
  WorkplaceDocumentItem,
  ArchiveStats,
  FolderNode
} from '../../services/api';
import FolderTree from './FolderTree';
import FileGrid from './FileGrid';
import DocumentPreviewModal from './DocumentPreviewModal';
import ArchiveUploadModal from './ArchiveUploadModal';
import Swal from 'sweetalert2';
import './ArchivePage.css';

// 18 Yasal İSG Evrak Türü Etiketleri
export const DOC_TYPE_LABELS: Record<string, string> = {
  riskAnalysis: '🛡️ Risk Değerlendirme Raporu',
  emergencyPlan: '🚨 Acil Durum Eylem Planı',
  trainingPlan: '📅 Yıllık Eğitim Planı',
  workPlan: '📋 Yıllık Çalışma Planı',
  instructions: '📄 İSG Talimatları & Prosedürler',
  ek2: '🩺 Ek-2 İşe Giriş & Periyodik Muayene',
  trainings: '🎓 Eğitimler ve Oryantasyon Belgeleri',
  employeeRepresentative: '👥 Çalışan Temsilcisi Seçim Tutanağı',
  annualEvaluation: '📊 Yıllık Değerlendirme Raporu',
  kkdDelivery: '🧤 KKD Teslim & Taahhüt Tutanağı',
  firstAid: '🩹 İlkyardımcı Belgesi & Dolap Kontrolü',
  machinePeriodicCheck: '⚙️ Makine & Ekipman Periyodik Kontrolleri',
  electricalInstallationCheck: '⚡ Elektrik & Topraklama Ölçüm Raporu',
  detectionSuggestionBook: '📖 Onaylı Defter / Tespit Öneri Defteri',
  emergencyDrill: '🔥 Acil Durum Tatbikat Raporu',
  fieldAuditReport: '📝 Saha Denetim & İSG Ziyaret Raporu',
  test: '🔬 Ortam Ölçümleri & Test Raporları',
  jobTraining: '📑 İşe Başlama & Kurul Tutanakları'
};

const ArchivePage: React.FC = () => {
  // Data State
  const [documents, setDocuments] = useState<WorkplaceDocumentItem[]>([]);
  const [stats, setStats] = useState<ArchiveStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [workplaces, setWorkplaces] = useState<Array<{ id: number; title: string; sgkNo: string; dangerClass: string; city?: string; district?: string }>>([]);

  // Folder Explorer State
  const [folderTree, setFolderTree] = useState<FolderNode[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [currentFolder, setCurrentFolder] = useState<FolderNode | null>(null);
  const [currentAncestors, setCurrentAncestors] = useState<Array<{ id: number; name: string; type: string }>>([]);
  const [currentFolderContent, setCurrentFolderContent] = useState<{
    subFolders: FolderNode[];
    documents: WorkplaceDocumentItem[];
  }>({ subFolders: [], documents: [] });
  const [folderLoading, setFolderLoading] = useState<boolean>(false);

  // Filters State
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Workplace Selector Dropdown
  const [isWpDropdownOpen, setIsWpDropdownOpen] = useState<boolean>(false);
  const [wpSearch, setWpSearch] = useState<string>('');
  const wpDropdownRef = useRef<HTMLDivElement>(null);

  // Modals State
  const [previewDoc, setPreviewDoc] = useState<WorkplaceDocumentItem | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);

  // Bulk Operations State
  const [selectedDocIds, setSelectedDocIds] = useState<number[]>([]);

  // 1. İşyerleri lookup listesini çek
  const fetchWorkplaces = async () => {
    try {
      const data = await archiveApi.getWorkplacesLookup();
      setWorkplaces(data || []);
    } catch (err) {
      console.error('İşyerleri listelenemedi:', err);
    }
  };

  // 2. İstatistikleri çek
  const fetchStats = async () => {
    try {
      const data = await archiveApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('İstatistikler yüklenemedi:', err);
    }
  };

  // 3. Belgeleri filtreye göre çek
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (selectedWorkplaceId && selectedWorkplaceId !== 'all') {
        params.workplaceId = selectedWorkplaceId;
      }
      if (selectedType && selectedType !== 'all') {
        params.documentType = selectedType;
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.expiryStatus = selectedStatus;
      }
      if (selectedYear && selectedYear !== 'all') {
        params.year = selectedYear;
      }
      if (searchTerm.trim()) {
        params.searchTerm = searchTerm.trim();
      }

      const docs = await archiveApi.getAllDocuments(params);
      setDocuments(docs || []);
    } catch (err) {
      console.error('Belgeler yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkplaceId, selectedType, selectedStatus, selectedYear, searchTerm]);

  // 4. Seçili işyeri için klasör ağacını çek
  const fetchFolderHierarchy = useCallback(async (workplaceId: number) => {
    setFolderLoading(true);
    try {
      const tree = await folderApi.getWorkplaceFolders(workplaceId);
      setFolderTree(tree || []);

      // Kök içerikleri yükle
      const rootData = await folderApi.getWorkplaceRootContent(workplaceId);
      setCurrentFolderContent(rootData);
      setSelectedFolderId(null);
      setCurrentFolder(null);
    } catch (err) {
      console.error('Klasör ağacı yüklenemedi:', err);
    } finally {
      setFolderLoading(false);
    }
  }, []);

  // 5. Belirli bir klasörün içeriğini çek
  const handleSelectFolder = async (folderId: number | null) => {
    setSelectedFolderId(folderId);
    setFolderLoading(true);

    try {
      if (folderId === null) {
        setCurrentFolder(null);
        setCurrentAncestors([]);
        if (selectedWorkplaceId !== 'all') {
          const rootData = await folderApi.getWorkplaceRootContent(parseInt(selectedWorkplaceId, 10));
          setCurrentFolderContent(rootData);
        }
      } else {
        const data = await folderApi.getFolderContent(folderId);
        setCurrentFolder(data.folder || null);
        setCurrentAncestors(data.ancestors || []);
        setCurrentFolderContent({
          subFolders: data.subFolders || [],
          documents: data.documents || []
        });
      }
    } catch (err) {
      console.error('Klasör içeriği yüklenemedi:', err);
    } finally {
      setFolderLoading(false);
    }
  };

  // İlk yükleme
  useEffect(() => {
    fetchWorkplaces();
    fetchStats();
    fetchDocuments();
  }, [fetchDocuments]);

  // İşyeri değiştiğinde klasör ağacını güncelle
  useEffect(() => {
    if (selectedWorkplaceId !== 'all') {
      fetchFolderHierarchy(parseInt(selectedWorkplaceId, 10));
    } else {
      setFolderTree([]);
      setSelectedFolderId(null);
    }
  }, [selectedWorkplaceId, fetchFolderHierarchy]);

  // Dropdown dışı tıklandığında kapat
  useEffect(() => {
    const handleDocClick = (e: MouseEvent) => {
      if (wpDropdownRef.current && !wpDropdownRef.current.contains(e.target as Node)) {
        setIsWpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  // Belge Önizleme
  const handlePreview = (doc: WorkplaceDocumentItem) => {
    setPreviewDoc(doc);
    setIsPreviewOpen(true);
  };

  // Belge İndirme
  const handleDownload = async (doc: WorkplaceDocumentItem) => {
    try {
      await archiveApi.downloadDocument(doc.id, doc.originalFileName);
    } catch (err) {
      console.error('İndirme hatası:', err);
      Swal.fire('Hata', 'Dosya indirilemedi.', 'error');
    }
  };

  // Belge Silme
  const handleDelete = async (doc: WorkplaceDocumentItem) => {
    const result = await Swal.fire({
      title: 'Belgeyi Sil',
      text: `"${doc.originalFileName}" arşivden kalıcı olarak silinecek. Emin misiniz?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'Vazgeç'
    });

    if (result.isConfirmed) {
      try {
        await archiveApi.deleteDocument(doc.id);
        Swal.fire({
          icon: 'success',
          title: 'Silindi',
          text: 'Belge arşivden kaldırıldı.',
          timer: 1500,
          showConfirmButton: false
        });
        fetchStats();
        fetchDocuments();
        if (selectedWorkplaceId !== 'all') {
          handleSelectFolder(selectedFolderId);
        }
      } catch (err) {
        console.error('Silme hatası:', err);
        Swal.fire('Hata', 'Belge silinirken bir sorun oluştu.', 'error');
      }
    }
  };

  // Toplu Seçim İşlemleri
  const handleToggleSelect = (docId: number) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAll = (allDocs: WorkplaceDocumentItem[]) => {
    if (selectedDocIds.length === allDocs.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(allDocs.map(d => d.id));
    }
  };

  // Toplu İndirme (ZIP)
  const handleBulkDownload = async () => {
    if (selectedDocIds.length === 0) return;
    try {
      Swal.fire({
        title: 'Hazırlanıyor...',
        text: `${selectedDocIds.length} adet belge arşivlenip ZIP olarak indiriliyor.`,
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      await archiveApi.bulkDownload(selectedDocIds);
      Swal.close();
    } catch (err) {
      console.error('Toplu indirme hatası:', err);
      Swal.fire('Hata', 'Toplu indirme sırasında bir hata oluştu.', 'error');
    }
  };

  // Toplu Silme
  const handleBulkDelete = async () => {
    if (selectedDocIds.length === 0) return;
    const result = await Swal.fire({
      title: 'Toplu Silme Onayı',
      text: `Seçili ${selectedDocIds.length} adet belge arşivden kalıcı olarak silinecek. Emin misiniz?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Hepsini Sil',
      cancelButtonText: 'Vazgeç'
    });

    if (result.isConfirmed) {
      try {
        await archiveApi.bulkDelete(selectedDocIds);
        Swal.fire({
          icon: 'success',
          title: 'Silindi',
          text: `${selectedDocIds.length} adet belge arşivden kaldırıldı.`,
          timer: 1500,
          showConfirmButton: false
        });
        setSelectedDocIds([]);
        fetchStats();
        fetchDocuments();
        if (selectedWorkplaceId !== 'all') {
          handleSelectFolder(selectedFolderId);
        }
      } catch (err) {
        console.error('Toplu silme hatası:', err);
        Swal.fire('Hata', 'Toplu silme sırasında bir sorun oluştu.', 'error');
      }
    }
  };

  // Yükleme Başarılı olduğunda yenile
  const handleUploadSuccess = () => {
    Swal.fire({
      icon: 'success',
      title: 'Belge Kaydedildi',
      text: 'Resmi evrak başarıyla yüklendi ve arşivlendi.',
      timer: 2000,
      showConfirmButton: false
    });
    fetchStats();
    fetchDocuments();
    if (selectedWorkplaceId !== 'all') {
      fetchFolderHierarchy(parseInt(selectedWorkplaceId, 10));
    }
  };

  const currentWorkplaceObj = workplaces.find(w => String(w.id) === selectedWorkplaceId);
  const filteredWorkplacesList = workplaces.filter(w =>
    (w.title || '').toLocaleLowerCase('tr-TR').includes(wpSearch.toLocaleLowerCase('tr-TR')) ||
    (w.sgkNo || '').includes(wpSearch)
  );

  const hasActiveFilter = Boolean(
    searchTerm.trim() ||
    selectedType !== 'all' ||
    selectedStatus !== 'all' ||
    selectedYear !== 'all'
  );

  const displayDocuments = (selectedWorkplaceId === 'all' || hasActiveFilter)
    ? documents
    : currentFolderContent.documents;

  const displaySubFolders = (selectedWorkplaceId === 'all' || hasActiveFilter)
    ? []
    : currentFolderContent.subFolders;

  return (
    <div className="archive-page-container">
      <div className="archive-page-content">
        {/* ==================================================================
            1. ÜST İSTATİSTİK ŞERİDİ (ISG DESTEK TEMASI)
            ================================================================== */}
        <div className="archive-stats-ribbon">
          <div className="stats-ribbon-item">
            <span className="ribbon-icon">🗄️</span>
            <div className="ribbon-info">
              <strong className="ribbon-value">{stats?.total || 0}</strong>
              <span className="ribbon-label">Toplam Belge</span>
            </div>
          </div>

          <div
            className={`stats-ribbon-item clickable ${selectedStatus === 'valid' ? 'active' : ''}`}
            onClick={() => setSelectedStatus(prev => prev === 'valid' ? 'all' : 'valid')}
            title="Geçerli belgeleri filtrele"
          >
            <span className="ribbon-icon text-green">✅</span>
            <div className="ribbon-info">
              <strong className="ribbon-value">{stats?.byStatus?.valid || 0}</strong>
              <span className="ribbon-label">Geçerli Belgeler</span>
            </div>
          </div>

          <div
            className={`stats-ribbon-item clickable ${selectedStatus === 'expiring' ? 'active' : ''}`}
            onClick={() => setSelectedStatus(prev => prev === 'expiring' ? 'all' : 'expiring')}
            title="Süresi yaklaşan belgeleri filtrele"
          >
            <span className="ribbon-icon text-amber">⚠️</span>
            <div className="ribbon-info">
              <strong className="ribbon-value">{stats?.byStatus?.expiring || 0}</strong>
              <span className="ribbon-label">Yaklaşan (&lt;30 Gün)</span>
            </div>
          </div>

          <div
            className={`stats-ribbon-item clickable ${selectedStatus === 'expired' ? 'active' : ''}`}
            onClick={() => setSelectedStatus(prev => prev === 'expired' ? 'all' : 'expired')}
            title="Süresi dolan belgeleri filtrele"
          >
            <span className="ribbon-icon text-red">❌</span>
            <div className="ribbon-info">
              <strong className="ribbon-value">{stats?.byStatus?.expired || 0}</strong>
              <span className="ribbon-label">Süresi Dolanlar</span>
            </div>
          </div>

          <div
            className={`stats-ribbon-item clickable ${selectedStatus === 'permanent' ? 'active' : ''}`}
            onClick={() => setSelectedStatus(prev => prev === 'permanent' ? 'all' : 'permanent')}
            title="Süresiz evrakları filtrele"
          >
            <span className="ribbon-icon text-blue">♾️</span>
            <div className="ribbon-info">
              <strong className="ribbon-value">{stats?.byStatus?.permanent || 0}</strong>
              <span className="ribbon-label">Süresiz / Güncel</span>
            </div>
          </div>
        </div>

        {/* ==================================================================
            2. MODERN KONTROL VE FİLTRELEME BARI
            ================================================================== */}
        <div className="archive-control-bar">
          {/* A. İşyeri Seçici Dropdown */}
          <div className="archive-wp-selector" ref={wpDropdownRef}>
            <div
              className="archive-wp-trigger"
              onClick={() => setIsWpDropdownOpen(!isWpDropdownOpen)}
              title="İşyeri arşivini seçin"
            >
              <span className="wp-trigger-icon">🏢</span>
              <span className="wp-trigger-text">
                {currentWorkplaceObj ? currentWorkplaceObj.title : 'Tüm İşyerleri (Genel Arşiv)'}
              </span>
              <span className="wp-trigger-arrow">▼</span>
            </div>

            {isWpDropdownOpen && (
              <div className="archive-wp-menu">
                <div className="archive-wp-search-input-box">
                  <input
                    type="text"
                    placeholder="İşyeri veya SGK ara..."
                    value={wpSearch}
                    onChange={(e) => setWpSearch(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className="archive-wp-items-list">
                  <div
                    className={`archive-wp-item ${selectedWorkplaceId === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedWorkplaceId('all');
                      setIsWpDropdownOpen(false);
                      setWpSearch('');
                    }}
                  >
                    <strong>🌐 Tüm İşyerleri (Genel Arşiv)</strong>
                    <small>Tüm belgeleri tek ekranda listeler</small>
                  </div>
                  {filteredWorkplacesList.map(w => (
                    <div
                      key={w.id}
                      className={`archive-wp-item ${String(w.id) === selectedWorkplaceId ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedWorkplaceId(String(w.id));
                        setIsWpDropdownOpen(false);
                        setWpSearch('');
                      }}
                    >
                      <strong>{w.title}</strong>
                      <small>SGK: {w.sgkNo || '-'} • {w.dangerClass === 'veryDangerous' ? 'Çok Tehlikeli' : w.dangerClass === 'dangerous' ? 'Tehlikeli' : 'Az Tehlikeli'}</small>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* B. Arama Kutusu */}
          <div className="archive-search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Dosya adı veya notlarda ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="clear-btn" onClick={() => setSearchTerm('')}>
                ✕
              </button>
            )}
          </div>

          {/* C. Evrak Türü Filtresi */}
          <div className="archive-filter-item">
            <select
              className="archive-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
            >
              <option value="all">📂 Tüm Evrak Türleri</option>
              {Object.entries(DOC_TYPE_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* D. Yıl Filtresi */}
          <div className="archive-filter-item">
            <select
              className="archive-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="all">📅 Tüm Yıllar</option>
              <option value="2026">2026</option>
              <option value="2025">2025</option>
              <option value="2024">2024</option>
              <option value="2023">2023</option>
            </select>
          </div>

          {/* E. Görünüm Değiştirici & Yeni Belge Butonu */}
          <div className="archive-actions-group">
            <div className="archive-view-toggle">
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Izgara Görünümü"
              >
                🪟
              </button>
              <button
                type="button"
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                title="Liste Görünümü"
              >
                📋
              </button>
            </div>

            <button
              type="button"
              className="archive-upload-btn"
              onClick={() => setIsUploadOpen(true)}
              title="Yeni Resmi İSG Belgesi Yükle"
            >
              <span className="btn-icon">📤</span>
              <span className="btn-text">Yeni Belge Yükle</span>
            </button>
          </div>
        </div>

        {/* ==================================================================
            3. ÇİFT PANELLİ KLASÖR GEZGİNİ & BELGE LİSTESİ
            ================================================================== */}
        <div className="archive-dual-pane-layout">
          {/* Sol Panel: Klasör Ağacı (Bir işyeri seçildiğinde görünür) */}
          {selectedWorkplaceId !== 'all' && (
            <div className="archive-sidebar-pane">
              <FolderTree
                rootFolders={folderTree}
                selectedFolderId={selectedFolderId}
                onSelectFolder={handleSelectFolder}
                workplaceTitle={currentWorkplaceObj?.title}
              />
            </div>
          )}

          {/* Sağ Panel: Klasör İçeriği ve Belgeler */}
          <div className="archive-main-pane">
            {/* Toplu İşlem Çubuğu (Seçili Belgeler Olduğunda Görünür) */}
            {selectedDocIds.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: '#1e293b',
                  color: '#ffffff',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  marginBottom: '10px',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: 600 }}>
                  <span>✓ <strong>{selectedDocIds.length}</strong> belge seçildi</span>
                  <button
                    type="button"
                    onClick={() => setSelectedDocIds([])}
                    style={{
                      background: 'transparent',
                      border: '1px solid #475569',
                      color: '#cbd5e1',
                      borderRadius: '4px',
                      padding: '2px 8px',
                      fontSize: '11px',
                      cursor: 'pointer'
                    }}
                  >
                    Seçimi Temizle
                  </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={handleBulkDownload}
                    style={{
                      background: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    📥 Toplu İndir (ZIP)
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    style={{
                      background: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    🗑️ Toplu Sil
                  </button>
                </div>
              </div>
            )}

            <FileGrid
              subFolders={displaySubFolders}
              documents={displayDocuments}
              onNavigate={handleSelectFolder}
              isLoading={selectedWorkplaceId !== 'all' && !hasActiveFilter ? folderLoading : loading}
              onPreview={handlePreview}
              onDownload={handleDownload}
              onDelete={handleDelete}
              docTypeLabels={DOC_TYPE_LABELS}
              viewMode={viewMode}
              currentFolder={hasActiveFilter ? null : currentFolder}
              ancestors={hasActiveFilter ? [] : currentAncestors}
              workplaceTitle={currentWorkplaceObj?.title}
              onSelectWorkplace={(wpId) => {
                setSelectedWorkplaceId(String(wpId));
                setSelectedFolderId(null);
              }}
              selectedDocIds={selectedDocIds}
              onToggleSelect={handleToggleSelect}
              onSelectAll={() => handleSelectAll(displayDocuments)}
            />
          </div>
        </div>
      </div>

      {/* Önizleme Modalı */}
      <DocumentPreviewModal
        document={previewDoc}
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewDoc(null);
        }}
        onDownload={handleDownload}
      />

      {/* Belge Yükleme Modalı */}
      <ArchiveUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onSuccess={handleUploadSuccess}
        workplaces={workplaces}
        selectedWorkplaceId={selectedWorkplaceId}
        docTypeLabels={DOC_TYPE_LABELS}
      />
    </div>
  );
};

export default ArchivePage;
